/**
 * MediumClipAsset - Represents a medium-length clip (60-180s)
 *
 * Medium clips are longer-form content extracted from the full video,
 * typically covering a complete topic or tutorial segment. Unlike shorts,
 * medium clips don't need platform variants (portrait/square) - they're
 * rendered in the original aspect ratio.
 */
import { VideoAsset } from './VideoAsset.js'
import { SocialPostAsset } from './SocialPostAsset.js'
import { join } from '../L1-infra/paths/paths.js'
import { fileExists, ensureDirectory } from '../L1-infra/fileSystem/fileSystem.js'
import type { MediumClip, Platform } from '../L0-pure/types/index.js'
import { Platform as PlatformEnum } from '../L0-pure/types/index.js'
import type { AssetOptions } from './Asset.js'
import { extractCompositeClip, applyIntroOutro } from '../L4-agents/videoServiceBridge.js'
import { generateThumbnailForClip } from './thumbnailGeneration.js'
import type { MainVideoAsset } from './MainVideoAsset.js'

/**
 * Asset representing a medium-length clip extracted from a longer video.
 *
 * Medium clips are 60-180 second segments that cover complete topics.
 * They're stored in a dedicated directory with their own captions and
 * social media posts.
 */
export class MediumClipAsset extends VideoAsset {
  /** Parent video this clip was extracted from */
  readonly parent: VideoAsset

  /** Clip metadata (start/end times, title, segments) */
  readonly clip: MediumClip

  /** Directory containing this clip's assets */
  readonly videoDir: string

  /** URL-safe identifier for this clip */
  readonly slug: string

  /**
   * Create a medium clip asset.
   *
   * @param parent - The source video this clip was extracted from
   * @param clip - Clip metadata including time ranges and title
   * @param clipsBaseDir - Base directory for all medium clips (e.g., recordings/{slug}/medium-clips)
   */
  constructor(parent: VideoAsset, clip: MediumClip, clipsBaseDir: string) {
    super()
    this.parent = parent
    this.clip = clip
    this.slug = clip.slug
    this.videoDir = join(clipsBaseDir, clip.slug)
  }

  // ── Paths ──────────────────────────────────────────────────────────────────

  /**
   * Path to the rendered clip video file.
   */
  get videoPath(): string {
    return join(this.videoDir, 'media.mp4')
  }

  /** Path to the clip with intro/outro applied */
  get introOutroVideoPath(): string {
    return join(this.videoDir, 'media-intro-outro.mp4')
  }

  /**
   * Directory containing social media posts for this clip.
   */
  get postsDir(): string {
    return join(this.videoDir, 'posts')
  }

  // ── Social Posts ───────────────────────────────────────────────────────────

  /**
   * Get social media posts for this medium clip as SocialPostAsset objects.
   * Returns one asset per platform.
   *
   * @returns Array of SocialPostAsset objects (one per platform)
   */
  async getSocialPosts(): Promise<SocialPostAsset[]> {
    const platforms: Platform[] = [
      PlatformEnum.TikTok,
      PlatformEnum.YouTube,
      PlatformEnum.Instagram,
      PlatformEnum.LinkedIn,
      PlatformEnum.X,
    ]
    return platforms.map((platform) => new SocialPostAsset(this, platform, this.postsDir))
  }

  // ── Asset Implementation ───────────────────────────────────────────────────

  /**
   * Check if the rendered clip exists on disk.
   */
  async exists(): Promise<boolean> {
    return fileExists(this.videoPath)
  }

  /**
   * Get the rendered clip video path, extracting from parent if needed.
   * Extracts from the enhanced video so AI-generated overlays carry through.
   *
   * @param opts - Asset options (force regeneration, etc.)
   * @returns Path to the rendered video file
   */
  async getResult(opts?: AssetOptions): Promise<string> {
    if (!opts?.force && (await this.exists())) {
      return this.videoPath
    }

    // Ensure output directory exists
    await ensureDirectory(this.videoDir)

    // Get enhanced video (with overlays, no captions — medium clips get their own captioning)
    const mainParent = this.parent as MainVideoAsset
    const parentVideo = await mainParent.getEnhancedVideo()

    // Extract clip using FFmpeg (handles single and composite segments)
    await extractCompositeClip(parentVideo, this.clip.segments, this.videoPath)

    return this.videoPath
  }

  /**
   * Apply intro/outro to the medium clip.
   * Uses brand config rules for 'medium-clips' video type.
   *
   * @returns Path to the intro/outro'd video, or the original path if skipped
   */
  async getIntroOutroVideo(): Promise<string> {
    if (await fileExists(this.introOutroVideoPath)) {
      return this.introOutroVideoPath
    }

    // Prefer the captioned version (has burned-in captions), then the raw clip
    const candidates = [this.clip.captionedPath, this.clip.outputPath]
    let clipPath: string | undefined
    for (const candidate of candidates) {
      if (candidate && await fileExists(candidate)) {
        clipPath = candidate
        break
      }
    }
    if (!clipPath) {
      clipPath = await this.getResult()
    }
    return applyIntroOutro(clipPath, 'medium-clips', this.introOutroVideoPath)
  }

  /**
   * Generate a thumbnail for this medium clip.
   *
   * Uses the ThumbnailAgent to plan and generate a click-worthy thumbnail
   * based on the clip's content. Skips if thumbnails are disabled or
   * a thumbnail already exists (idempotent).
   *
   * @param opts - Asset options (force to regenerate)
   * @returns Path to the generated thumbnail, or null if skipped
   */
  async generateThumbnail(opts?: AssetOptions): Promise<string | null> {
    const videoPath = this.clip.captionedPath ?? this.clip.outputPath
    const thumbnailDir = join(this.videoDir, 'thumbnails')

    const result = await generateThumbnailForClip({
      title: this.clip.title,
      description: this.clip.description,
      hook: this.clip.hook,
      topics: this.clip.tags,
      videoPath,
      outputDir: thumbnailDir,
      contentType: 'medium-clips',
    }, opts?.force)

    if (result) {
      this.clip.thumbnailPath = result
    }

    return result
  }
}
